import SharedBlock from '~/data/strapi/enums/shared-block';
import Article from '~/data/strapi/types/collection-types/article';
import { RegionLocale } from '~/infra/locale';

import strapiClient, { StrapiCollectionResponse } from '../../../client';
import seoPopulate from '../../populates/seo';

interface FetchArticlesParams {
  locale: RegionLocale;
  tagSlug?: string | null;
  page?: number;
  pageSize?: number;
  start?: number;
  limit?: number;
}

type StrapiPopulate =
  | string
  | string[]
  | {
      [key: string]: StrapiPopulate | boolean | '*' | { populate?: StrapiPopulate; fields?: string[] };
    };

type StrapiFilterValue = string | number | boolean | null | string[] | number[] | StrapiFilters;

interface StrapiFilters {
  [key: string]: StrapiFilterValue | { [operator: string]: StrapiFilterValue };
}

interface StrapiRequestParams {
  populate?: StrapiPopulate;
  [key: string]: number | string | StrapiPopulate | StrapiFilters | undefined;
  sort?: string | string[];
  filters?: StrapiFilters;
}

const fetchArticles = async ({
  locale,
  tagSlug,
  page,
  pageSize,
  start,
  limit,
}: FetchArticlesParams): Promise<StrapiCollectionResponse<Article>> => {
  const params: StrapiRequestParams = {
    locale,
    populate: {
      image: { populate: '*' },
      articleTags: { populate: '*' },
    },
    sort: ['date:desc', 'id:desc'],
  };

  if (page) {
    params['pagination[page]'] = page;
    params['pagination[pageSize]'] = pageSize || 12;
  }

  if (start) {
    params['pagination[start]'] = start;
    params['pagination[limit]'] = limit || 12;
  }

  if (tagSlug) {
    params.filters = {
      articleTags: {
        slug: {
          $eq: tagSlug,
        },
      },
    };
  }

  const data = await strapiClient.get<StrapiCollectionResponse<Article>>('/articles', {
    params,
  });
  return data;
};

const fetchArticleById = async (id: string | number, locale: RegionLocale): Promise<Article> => {
  const data = await strapiClient.get<Article>(`/articles/slug/${id}`, {
    params: {
      locale,
      populate: {
        seo: seoPopulate(),
        articleTags: {
          populate: '*',
        },
        image: {
          populate: '*',
        },
        blocks: {
          on: {
            [SharedBlock.CUSTOMER_FORM]: {
              populate: {
                heading: { populate: '*' },
                image: { populate: '*' },
              },
            },
            [SharedBlock.WYSIWYG_CONTENT]: {
              populate: '*',
            },
            [SharedBlock.OTHER_ITEMS]: {
              populate: '*',
            },
          },
        },
      },
    },
  });

  return data;
};

export { fetchArticles, fetchArticleById };
